import React, { useState } from 'react';
import { motion } from 'framer-motion';
import ObliqueIcon from '@/components/icons/ObliqueIcon';

export function Avatar({ 
    src, 
    initials, 
    size = 'md', 
    status, // 'online', 'offline', 'busy', 'away'
    className = '' 
}) {
    const [imgError, setImgError] = useState(false);

    const sizeClasses = {
        xs: 'w-6 h-6 text-xs',
        sm: 'w-8 h-8 text-sm',
        md: 'w-10 h-10 text-base',
        lg: 'w-12 h-12 text-lg',
        xl: 'w-16 h-16 text-xl'
    };

    const statusColors = {
        online: 'bg-green-500',
        offline: 'bg-gray-400',
        busy: 'bg-red-500',
        away: 'bg-amber-500'
    };

    const statusSizes = {
        xs: 'w-1.5 h-1.5 border',
        sm: 'w-2 h-2 border-2',
        md: 'w-2.5 h-2.5 border-2',
        lg: 'w-3 h-3 border-2',
        xl: 'w-4 h-4 border-2'
    };

    const getAvatarColor = (name) => {
        if (!name) return 'var(--bg-subtle)';
        const colors = [
            '#ef4444', '#f97316', '#f59e0b', '#84cc16', '#22c55e', '#10b981', '#14b8a6', 
            '#06b6d4', '#0ea5e9', '#3b82f6', '#6366f1', '#8b5cf6', '#a855f7', '#d946ef', '#ec4899', '#f43f5e'
        ];
        let hash = 0;
        for (let i = 0; i < name.length; i++) {
            hash = name.charCodeAt(i) + ((hash << 5) - hash);
        }
        return colors[Math.abs(hash) % colors.length];
    };

    const bgColor = (!src || imgError) && initials ? getAvatarColor(initials) : 'var(--bg-subtle)';

    const renderContent = () => {
        if (src && !imgError) {
            return (
                <img 
                    src={src} 
                    alt={initials || 'Avatar'} 
                    className="w-full h-full object-cover"
                    onError={() => setImgError(true)}
                />
            );
        }
        
        if (initials) {
            return <span className="font-semibold text-white tracking-wide">{initials.substring(0, 2).toUpperCase()}</span>;
        }

        return <ObliqueIcon name="person" className="text-[var(--text-tertiary)] opacity-50" size={size === 'xs' ? 14 : size === 'sm' ? 16 : 24} />;
    };

    return (
        <div className={`relative inline-flex ${className}`}>
            <motion.div 
                layout
                className={`
                    flex items-center justify-center rounded-full overflow-hidden shrink-0 border border-black/10 shadow-sm
                    ${sizeClasses[size]}
                `}
                style={{ backgroundColor: bgColor }}
            >
                {renderContent()}
            </motion.div>
            
            {status && (
                <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className={`
                        absolute bottom-0 right-0 rounded-full
                        border-white dark:border-gray-900 shadow-sm
                        ${statusColors[status]} 
                        ${statusSizes[size]}
                    `}
                />
            )}
        </div>
    );
}
